// LUMINA · pages/vajra/components/GuardianWidget.jsx
// Phase 39D: extracted from VajraPortal.jsx — Floating AI advisor

import React, { useState, useEffect, useRef } from 'react';
import { API, GD, GDB, GDL, TXT, TXT2 } from '../constants';

export default function GuardianWidget({ token, member, energyInsight }) {
  const [open, setOpen]         = useState(false);
  const [guardianMe, setGMe]    = useState(null);
  const [msgs, setMsgs]         = useState([{
    role:"assistant",
    text:"☸ 愿吉祥如意。我是您的护法引导者，可以询问五行对治、法会推荐或月运护持。\n\n试试 /remedy（五行补救）或 /forecast（本月护持）"
  }]);
  const [input, setInput]       = useState("");
  const [streaming, setStream]  = useState(false);
  const bottomRef               = useRef(null);
  const abortRef                = useRef(null);

  // Fetch guardian/me when panel opens
  useEffect(() => {
    if (!open || !token) return;
    fetch(`${API}/vajra/guardian/me`, {
      headers:{"Authorization":`Bearer ${token}`}
    }).then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setGMe(d); })
      .catch(() => {});
  }, [open, token]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior:"smooth" });
  }, [msgs]);

  const linked     = guardianMe?.linked      ?? energyInsight?.linked      ?? false;
  const stability  = guardianMe?.stability   ?? 70;
  const isDisciple = guardianMe?.is_disciple ?? member?.is_disciple        ?? false;
  const pulse      = stability < 50;

  const sendMsg = async (text) => {
    if (!text.trim() || streaming) return;
    setMsgs(prev => [...prev, {role:"user",text}, {role:"assistant",text:"",loading:true}]);
    setInput(""); setStream(true);
    try {
      abortRef.current = new AbortController();
      const resp = await fetch(`${API}/vajra/guardian/chat`, {
        method:"POST",
        headers:{"Authorization":`Bearer ${token}`,"Content-Type":"application/json"},
        body: JSON.stringify({ message:text, session_id:`guardian_${member.id}` }),
        signal: abortRef.current.signal,
      });
      if (!resp.ok) {
        const e = await resp.json().catch(()=>({detail:"请求失败"}));
        setMsgs(prev=>{const n=[...prev];n[n.length-1]={role:"assistant",text:`⚠ ${e.detail||"连接失败"}`};return n;});
        setStream(false); return;
      }
      const reader  = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const {done,value} = await reader.read();
        if (done) break;
        buf += decoder.decode(value,{stream:true});
        const lines = buf.split("\n"); buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (raw==="[DONE]") break;
          try {
            const chunk = JSON.parse(raw);
            const delta = chunk.choices?.[0]?.delta?.content ?? chunk.text ?? "";
            if (delta) setMsgs(prev=>{
              const n=[...prev]; const last=n[n.length-1];
              n[n.length-1]={...last,text:(last.text||"")+delta,loading:false}; return n;
            });
          } catch {}
        }
      }
    } catch(err) {
      if (err.name!=="AbortError")
        setMsgs(prev=>{const n=[...prev];n[n.length-1]={role:"assistant",text:"⚠ 连接中断，请重试"};return n;});
    }
    setStream(false);
  };

  const handleKey = (e) => {
    if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); sendMsg(input); }
  };

  // Mandala SVG watermark
  const MandalaSVG = () => (
    <svg viewBox="0 0 200 200" style={{position:"absolute",top:"50%",left:"50%",
      transform:"translate(-50%,-50%)",width:180,height:180,opacity:0.048,
      pointerEvents:"none",zIndex:0}}>
      <circle cx="100" cy="100" r="90" fill="none" stroke="#B8860B" strokeWidth="1"/>
      <circle cx="100" cy="100" r="70" fill="none" stroke="#B8860B" strokeWidth="0.5"/>
      <circle cx="100" cy="100" r="50" fill="none" stroke="#B8860B" strokeWidth="0.5"/>
      {[0,45,90,135,180,225,270,315].map(a=>{
        const rad=a*Math.PI/180;
        return <line key={a}
          x1={100+Math.cos(rad)*50} y1={100+Math.sin(rad)*50}
          x2={100+Math.cos(rad)*90} y2={100+Math.sin(rad)*90}
          stroke="#B8860B" strokeWidth="0.5"/>;
      })}
      {[0,60,120,180,240,300].map(a=>{
        const rad=a*Math.PI/180;
        return <circle key={a}
          cx={100+Math.cos(rad)*70} cy={100+Math.sin(rad)*70}
          r="6" fill="none" stroke="#B8860B" strokeWidth="0.5"/>;
      })}
      <circle cx="100" cy="100" r="8" fill="none" stroke="#B8860B" strokeWidth="1"/>
      <text x="100" y="104" textAnchor="middle" fontSize="10" fill="#B8860B" fontFamily="serif">☸</text>
    </svg>
  );

  const dotColor = stability>=75?"#34d399":stability>=50?GD:"#f87171";

  return (
    <>
      {/* Pulse keyframe inject */}
      <style>{`
        @keyframes gPulse {
          0%,100%{opacity:1;box-shadow:0 4px 20px rgba(107,26,26,0.5);}
          50%{opacity:.78;box-shadow:0 4px 30px rgba(248,113,113,0.55);}
        }
        @keyframes gDotPulse {
          0%,100%{opacity:1;transform:scale(1);}
          50%{opacity:.5;transform:scale(1.4);}
        }
      `}</style>

      {/* Floating trigger button */}
      <button onClick={()=>setOpen(o=>!o)} style={{
        position:"fixed",bottom:28,right:28,zIndex:9999,
        width:52,height:52,borderRadius:"50%",
        background:`linear-gradient(135deg,${GD},${GDL})`,
        border:`2px solid ${GDB}`,
        color:"#fff",fontSize:22,cursor:"pointer",
        boxShadow:"0 4px 20px rgba(139,105,20,0.4)",
        display:"flex",alignItems:"center",justifyContent:"center",
        animation:pulse?"gPulse 2.5s ease-in-out infinite":"none",
        transition:"transform .2s",
        outline:"none",
      }} title="护法引导">
        {open?"✕":"☸"}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position:"fixed",bottom:92,right:28,zIndex:9998,
          width:340,height:480,
          background:"rgba(253,251,247,0.97)",
          backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",
          border:`1px solid rgba(184,134,11,0.35)`,
          borderRadius:16,
          boxShadow:"0 8px 40px rgba(0,0,0,0.55),0 0 0 1px rgba(184,134,11,0.10)",
          display:"flex",flexDirection:"column",
          overflow:"hidden",
          fontFamily:"'Noto Serif SC','Noto Serif',serif",
        }}>
          <MandalaSVG/>

          {/* Header */}
          <div style={{
            padding:"11px 16px",
            borderBottom:"1px solid rgba(184,134,11,0.15)",
            display:"flex",justifyContent:"space-between",alignItems:"center",
            flexShrink:0,position:"relative",zIndex:1,
          }}>
            <div>
              <div style={{color:GDL,fontSize:13,letterSpacing:2}}>护法引导</div>
              {isDisciple && (
                <div style={{fontSize:9,color:"rgba(212,175,55,0.55)",letterSpacing:1,marginTop:1}}>
                  传承加持模式
                </div>
              )}
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <div style={{
                width:7,height:7,borderRadius:"50%",background:dotColor,
                animation:pulse?"gDotPulse 2s ease-in-out infinite":"none",
              }}/>
              <span style={{fontSize:10,color:"rgba(255,255,255,0.28)",letterSpacing:.5}}>
                稳定 {stability}
              </span>
            </div>
          </div>

          {/* Not linked: show gate */}
          {!linked ? (
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",
              justifyContent:"center",padding:28,textAlign:"center",position:"relative",zIndex:1}}>
              <div style={{fontSize:36,marginBottom:14,color:GDL}}>☸</div>
              <div style={{color:"rgba(255,255,255,0.55)",fontSize:13,lineHeight:1.9}}>
                请先在能量审计页面<br/>
                完成年丽命盘分析<br/>
                以关联您的五行命盘
              </div>
              <div style={{marginTop:14,fontSize:11,color:"rgba(184,134,11,0.45)",letterSpacing:.5}}>
                护法引导依托命盘数据
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div style={{flex:1,overflowY:"auto",padding:"12px 14px",
                display:"flex",flexDirection:"column",gap:9,position:"relative",zIndex:1}}>
                {msgs.map((m,i)=>(
                  <div key={i} style={{alignSelf:m.role==="user"?"flex-end":"flex-start",maxWidth:"86%"}}>
                    <div style={{
                      padding:"8px 12px",
                      borderRadius:m.role==="user"?"12px 12px 3px 12px":"12px 12px 12px 3px",
                      background:m.role==="user"
                        ?`linear-gradient(135deg,${GD},${GDL})`
                        :"rgba(139,105,20,0.06)",
                      border:m.role==="assistant"?`1px solid rgba(212,175,55,0.2)`:"none",
                      color:m.role==="user"?"#fff":m.loading?TXT2:TXT,
                      fontSize:12.5,lineHeight:1.75,whiteSpace:"pre-wrap",
                    }}>
                      {m.loading?"⋯":m.text}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef}/>
              </div>

              {/* Slash command shortcuts */}
              {!streaming && (
                <div style={{padding:"0 14px 7px",display:"flex",gap:6,
                  flexWrap:"wrap",position:"relative",zIndex:1}}>
                  {["/remedy","/forecast"].map(cmd=>(
                    <button key={cmd} onClick={()=>sendMsg(cmd)} style={{
                      fontSize:11,padding:"3px 10px",borderRadius:10,cursor:"pointer",
                      background:"rgba(184,134,11,0.08)",
                      border:"1px solid rgba(184,134,11,0.22)",
                      color:GDL,letterSpacing:.5,outline:"none",
                    }}>{cmd}</button>
                  ))}
                </div>
              )}

              {/* Input row */}
              <div style={{padding:"8px 14px 14px",
                borderTop:"1px solid rgba(184,134,11,0.10)",
                flexShrink:0,display:"flex",gap:8,
                position:"relative",zIndex:1}}>
                <textarea
                  value={input}
                  onChange={e=>setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="问修行护持…"
                  rows={1}
                  disabled={streaming}
                  style={{
                    flex:1,resize:"none",
                    background:"rgba(255,255,255,0.85)",
                    border:`1px solid rgba(212,175,55,0.3)`,
                    borderRadius:8,
                    color:TXT,
                    fontSize:13,padding:"7px 10px",
                    outline:"none",fontFamily:"inherit",lineHeight:1.5,
                  }}
                />
                <button onClick={()=>sendMsg(input)}
                  disabled={streaming||!input.trim()} style={{
                  width:36,flexShrink:0,
                  background:streaming||!input.trim()
                    ?"rgba(139,105,20,0.08)"
                    :`linear-gradient(135deg,${GD},${GDL})`,
                  border:"none",borderRadius:8,
                  cursor:streaming||!input.trim()?"default":"pointer",
                  color:streaming||!input.trim()?TXT2:"#fff",fontSize:16,
                  opacity:(!input.trim()||streaming)?0.35:1,
                  outline:"none",
                }}>↑</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
